import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/Email";
import AddIcon from "@mui/icons-material/Add";
import { TriggerConditions } from "./TriggerConditions";
import { EmailConfiguration } from "./EmailConfiguration";
import { TemplateConfiguration } from "./TemplateConfiguration";
import { useAutomationFlowEditor } from "./useAutomationFlowEditor";

export const AutomationFlowEditor: React.FC = () => {
  const {
    name,
    setName,
    description,
    setDescription,
    conditions,
    conditionsLogic,
    emailAction,
    setEmailAction,
    templateAction,
    setTemplateAction,
    activeActions,
    saving,
    testing,
    testResult,
    setTestResult,
    isEditing,
    error,
    clearError,
    handleConditionsChange,
    handleBack,
    handleAddTemplate,
    handleAddEmail,
    handleRemoveAction,
    handleSave,
    handleTest,
  } = useAutomationFlowEditor();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant="text"
        >
          Back to Flows
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={testing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          onClick={handleTest}
          disabled={testing || !isEditing}
        >
          Test Flow
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Save Automation
        </Button>
      </Box>

      {/* Title Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Automation Flow Editor
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create automated workflows with IF/THEN logic
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {testResult && (
        <Alert
          severity={testResult.conditions_met ? "success" : "warning"}
          onClose={() => setTestResult(null)}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            <strong>Test Result:</strong>{" "}
            {testResult.conditions_met
              ? "Conditions would be met"
              : "Conditions would NOT be met"}
          </Typography>
          {testResult.simulated_actions.map((action, i) => (
            <Typography key={i} variant="caption" display="block">
              - {action.action}: {action.would_execute ? "Would execute" : "Would not execute"}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Main Content */}
      <Paper sx={{ flex: 1, overflow: "auto", p: 3 }}>
        {/* Flow Name and Description */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            label="Flow Name"
            placeholder="Enter a name for this flow"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Description (optional)"
            placeholder="Describe what this flow does"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ flex: 2 }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* IF - Trigger Conditions */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Chip label="IF" color="primary" size="small" sx={{ fontWeight: 600 }} />
            <Typography variant="subtitle1" fontWeight={500}>
              Trigger & Logic
            </Typography>
          </Box>
          <TriggerConditions
            conditions={conditions}
            conditionsLogic={conditionsLogic}
            onChange={handleConditionsChange}
          />
        </Paper>

        {/* Arrow connector */}
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <ArrowDownwardIcon color="action" fontSize="large" />
        </Box>

        {/* THEN - Execution Section */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Chip label="THEN" color="success" size="small" sx={{ fontWeight: 600 }} />
            <Typography variant="subtitle1" fontWeight={500}>
              Execution & Content
            </Typography>
          </Box>

          {/* Action buttons - always visible */}
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={handleAddTemplate}
              disabled={activeActions.includes("template")}
            >
              Add Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleAddEmail}
              disabled={activeActions.includes("email")}
            >
              Add Email
            </Button>
          </Box>

          {/* No actions selected message */}
          {activeActions.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                bgcolor: "grey.50",
                borderRadius: 1,
              }}
            >
              <AddIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
              <Typography color="text.secondary">
                Click "Add Template" or "Add Email" to add actions
              </Typography>
            </Box>
          )}

          {/* Template Configuration */}
          {activeActions.includes("template") && (
            <Box sx={{ mb: 2 }}>
              <TemplateConfiguration
                templateAction={templateAction}
                onChange={setTemplateAction}
                onRemove={() => handleRemoveAction("template")}
              />
            </Box>
          )}

          {/* Email Configuration */}
          {activeActions.includes("email") && (
            <EmailConfiguration
              emailAction={emailAction}
              onChange={setEmailAction}
              onRemove={() => handleRemoveAction("email")}
            />
          )}
        </Paper>

      </Paper>
    </Box>
  );
};
